import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Grid, Typography } from "@mui/material";
import Layout from "../../components/Layout/Layout";
import QueryPageTitleCard from "../../components/QueryPageTitleCard/QueryPageTitleCard";
import CardTitleWithSubtitle from "../../components/CardTitleWithSubtitle/CardTitleWithSubtitle";
import CustomCard from "../../components/CustomCard/CustomCard";
import MultipleSelectDistributors from "../../components/MultipleSelectDistributors/MultipleSelectDistributors";
import SelectSubsidiaries from "../../components/SelectSubsidiary/SelectSubsidiary";
import { useDistributor } from "../../contexts/distributor/DistributorContext";
import { useIocContext } from "../../contexts/ioc/IocContext";
import { ISupplyService } from "../../modules/supply/models/ISupplyService";
import { Types } from "../../ioc/types";
import useDialogAlert from "../../hooks/DialogAlert";
import AppError from "../../utils/appError";
import MultipleSelectCheckbox from "../../components/MultipleSelectCheckbox";
import { convertToOptionsList } from "../../utils";
import CustomButton, {
  CustomButtonVariant,
} from "../../components/CustomButton/CustomButton";
import MonthYearPicker from "../../components/MonthYearPicker/MonthYearPicker";
import {
  formatMinMaxDate,
  parseMonthYearString,
} from "../../utils/dateFormatter";
import { StorageTable } from "./shared/StorageTable";
import { IOperationalService } from "../../modules/operational/models/IOperationalService";
import { GetContractsDTO } from "../../modules/operational/models/GetContractsDTO";
import { format } from "date-fns";
import { useBalanceStorage } from "./shared/useBalanceStorage";
import { IStorageService } from "../../modules/storage/models/IStorageService";
import TextFieldCustom from "../../components/TextFieldCustom/TextFieldCustom";
import { StyledIcon } from "../../components/StyledIcon/StyledIcon";
import Select from "../../components/Select/Select";

const StorageQuoteBalance: React.FC = () => {
  const [codeSubsidiary, setCodeSubsidiary] = useState<string | undefined>(
    undefined
  );
  const [codeDistributor, setCodeDistributor] = useState<string | null>(null);
  const [codePeriod, setCodePeriod] = useState<string | null>(null);
  const [period, setPeriod] = useState<Array<Date | null>>([]);
  const [contracts, setContracts] = useState<GetContractsDTO>([]);
  const [codeContract, setCodeContract] = useState<string[] | string>([]);
  const [searchNf, setSearchNf] = useState<string>("");
  const [searchSeries, setSearchSeries] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPeriod, setIsLoadingPeriod] = useState(false);
  const [isLoadingContract, setIsLoadingContract] = useState(false);

  const { selectedDistributorsStoraged, subsidiaries } = useDistributor();
  const { serviceContainer } = useIocContext();

  const supplyService = serviceContainer.get<ISupplyService>(
    Types.Supply.ISupplyService
  );

  const operationalService = serviceContainer.get<IOperationalService>(
    Types.Operational.IOperationalService
  );

  const storageService = serviceContainer.get<IStorageService>(
    Types.Storage.IStorageService
  );

  const { snackbar } = useDialogAlert();

  const getPeriodData = useCallback(async (id: string) => {
    try {
      setIsLoadingPeriod(true);
      const response = await supplyService.getAllPeriodByCompany?.(id);
      const sortedResponse = response.sort((a, b) => {
        const monthA = a.monthReference.split("-")[1];
        const monthB = b.monthReference.split("-")[1];

        return monthA.localeCompare(monthB);
      });

      const [firstMonth, lastMonth] = formatMinMaxDate(sortedResponse);
      setPeriod([firstMonth, lastMonth]);
    } catch (error) {
      snackbar({
        message: `Erro ao buscar dados do período: ${
          (error as AppError).message
        }`,
        variant: "error",
      });
    } finally {
      setIsLoadingPeriod(false);
    }
  }, []);

  const getContracts = async () => {
    try {
      setIsLoadingContract(true);
      const currentMonthYear = format(new Date(), "MM-yyyy");

      const response = await operationalService.getContracts(
        selectedDistributorsStoraged.length
          ? selectedDistributorsStoraged.join(",")
          : "",
        codeSubsidiary ?? "",
        undefined,
        {
          monthReferenceFrom: codePeriod ?? currentMonthYear,
          monthReferenceTo: codePeriod ?? currentMonthYear,
        }
      );

      if (!response.length) {
        snackbar({
          message: "Nenhum contrato encontrado para os filtros selecionados",
          variant: "error",
        });
      }

      setContracts(response);
    } catch (error) {
      setContracts([]);
      snackbar({
        message: "Erro ao buscar os contratos",
        variant: "error",
      });
    } finally {
      setIsLoadingContract(false);
      setCodeContract([]);
    }
  };

  useEffect(() => {
    if (canGetContract) {
      getContracts();
    }
  }, [codeSubsidiary, selectedDistributorsStoraged, codePeriod]);

  const handleChangeSelectedCompanies = useCallback(
    (event: string[]) => {
      if (event?.length > 0) {
        setCodeContract(event);
      }
    },
    [setCodeContract, contracts]
  );

  useEffect(() => {
    if (selectedDistributorsStoraged.length !== 0) {
      setCodeDistributor(selectedDistributorsStoraged.join(","));
      getPeriodData(selectedDistributorsStoraged.join(","));
    }
  }, [selectedDistributorsStoraged]);

  const canGetContract = useMemo(() => {
    return (
      !!codeSubsidiary &&
      !!selectedDistributorsStoraged.length &&
      !!codePeriod &&
      !isLoadingContract
    );
  }, [codeSubsidiary, selectedDistributorsStoraged.length, codePeriod]);

  const hasError = useMemo(() => {
    return (!period[0] || !period[1]) && !isLoadingPeriod && !!codeDistributor;
  }, [period, isLoading]);

  const disabled = useMemo(() => {
    return !codeDistributor || !period[0] || !period[1] || isLoadingPeriod;
  }, [codeDistributor, period, isLoading]);

  const balanceStorage = useBalanceStorage(
    { storageService, snackbar },
    { isLoading, setIsLoading }
  );

  const {
    tab,
    page,
    appliedFilters,
    hasAppliedFilters,
    balanceList,
    handlePageChange,
    handleTabChange,
    handleApplyFilters,
    handleClearList,
  } = balanceStorage;

  useEffect(() => {
    setCodeContract(tab === 1 ? "" : []);
    handleClearList();
  }, [tab]);

  const filters = useMemo(() => {
    return {
      cnpjs: selectedDistributorsStoraged,
      contractId: Array.isArray(codeContract)
        ? codeContract.join(",")
        : codeContract,
      date: codePeriod ?? "",
      orderNumber: tab === 1 ? searchNf.trim() : "",
      orderSeries: tab === 1 ? searchSeries.trim() : "",
    };
  }, [
    selectedDistributorsStoraged,
    searchNf,
    searchSeries,
    codeContract,
    codePeriod,
    tab,
  ]);

  const handleOnSearch = () => {
    handleApplyFilters(filters);
  };

  const handleOnClear = () => {
    setContracts([]);
    setCodeContract([]);
    setCodePeriod(null);
    setCodeSubsidiary(undefined);
    setSearchNf("");
    setSearchSeries("");
    handleClearList();
  };

  const canFetch = useMemo(() => {
    if (tab === 1) {
      return !disabled && !!codeContract.length && !!searchNf.trim();
    }
    return !disabled && !!codeContract.length;
  }, [disabled, codeContract, tab, searchNf]);

  return (
    <Layout
      enableMargin
      appBarProps={{
        notification: {
          onChange() {},
        },
        chat: {
          onChange() {},
        },
        settings: {
          onChange() {},
        },
        avatar: () => (
          <Avatar
            alt="Avatar"
            src="/images/avatar.png"
            sx={{
              width: 60,
              height: 60,
            }}
          />
        ),
      }}
    >
      <Grid item xs={12}>
        <QueryPageTitleCard title="Consultar saldo" />
      </Grid>
      <Grid item xs={12} sx={{ marginTop: "24px" }}>
        <CustomCard
          sx={{
            boxShadow: "none",
            border: "1px solid #DFE0EB",
            padding: 2.4,
          }}
        >
          <CardTitleWithSubtitle
            title="Consultar saldo de cota"
            subtitle="Informe os parâmetros de pesquisa para consultar o saldo."
          />
          <Grid container>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MultipleSelectDistributors label="Selecione o cliente" />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SelectSubsidiaries
                    label="Filial"
                    valueSubsidiary={subsidiaries.find(
                      (item) => item.id === codeSubsidiary
                    )}
                    onChangeValue={(selectedOption) => {
                      if (selectedOption) {
                        setCodeSubsidiary(selectedOption.id);
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MonthYearPicker
                    fullWidth={true}
                    label="Período"
                    minDate={new Date(2026, 0, 1)}
                    maxDate={new Date(new Date().getFullYear(), 11, 31)}
                    value={parseMonthYearString(codePeriod ?? "")}
                    onChange={(date) => {
                      if (!date) return;
                      const month = date.getMonth() + 1;
                      const year = date.getFullYear();
                      setCodePeriod(
                        `${month.toString().padStart(2, "0")}-${year}`
                      );
                    }}
                    disabled={disabled}
                    hasError={hasError}
                    messageError={
                      hasError
                        ? "Não há períodos disponíveis para o cliente"
                        : ""
                    }
                    views={["month", "year"]}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  {tab === 1 ? (
                    <Select
                      label="Contratos"
                      options={contracts.map((option) => ({ option }))}
                      getOptionItemLabel={(value) => value?.label ?? ""}
                      getSelectedOptionLabel={(value) => value?.label ?? ""}
                      onChange={(value) => {
                        setCodeContract(value?.id ?? "");
                      }}
                      value={
                        typeof codeContract === "string" && codeContract
                          ? contracts.find((item) => item.id === codeContract)
                          : null
                      }
                      isDisabled={!canGetContract || contracts.length === 0}
                    />
                  ) : (
                    <>
                      <MultipleSelectCheckbox
                        label="Contratos"
                        isDisabled={!canGetContract || contracts.length === 0}
                        options={convertToOptionsList(contracts, "id", "label")}
                        value={Array.isArray(codeContract) ? codeContract : []}
                        onChange={handleChangeSelectedCompanies}
                      />
                    </>
                  )}
                </Grid>

                {tab === 1 && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        sx={{
                          color: "#58595B",
                          fontFamily: "Open Sans",
                          fontSize: "14px",
                          fontWeight: "700",
                          paddingBottom: "4px",
                        }}
                      >
                        NF
                      </Typography>
                      <TextFieldCustom
                        placeholder="Informe a NF"
                        value={searchNf}
                        onChange={(e) => setSearchNf(e?.target?.value ?? "")}
                        endComponent={<StyledIcon iconType="search" />}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Typography
                        sx={{
                          color: "#58595B",
                          fontFamily: "Open Sans",
                          fontSize: "14px",
                          fontWeight: "700",
                          paddingBottom: "4px",
                        }}
                      >
                        Série
                      </Typography>
                      <TextFieldCustom
                        placeholder="Informe a Série"
                        value={searchSeries}
                        onChange={(e) =>
                          setSearchSeries(e?.target?.value ?? "")
                        }
                        endComponent={<StyledIcon iconType="search" />}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              container
              alignItems="flex-end"
              justifyContent={"flex-end"}
              alignContent={"flex-start"}
              marginTop={"25px"}
              marginBottom={"25px"}
            >
              <Grid item>
                <CustomButton
                  title="Limpar filtros"
                  variant={
                    hasAppliedFilters
                      ? CustomButtonVariant.OUTLINED
                      : CustomButtonVariant.DISABLED
                  }
                  onClick={handleOnClear}
                />
              </Grid>
              <Grid item>
                <CustomButton
                  title="Consultar"
                  onClick={handleOnSearch}
                  variant={
                    canFetch
                      ? CustomButtonVariant.CONTAINED
                      : CustomButtonVariant.DISABLED
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          <StorageTable
            {...{
              handleTabChange,
              handlePageChange,
              tab,
              page,
              balanceList,
              appliedFilters,
              isLoading,
            }}
          />
        </CustomCard>
      </Grid>
    </Layout>
  );
};

export default StorageQuoteBalance;
